import React, { useMemo } from "react";
import {
  DetalleTransporteDeFecha,
  ModificacionesDetalleTransporte,
  QUERY_OBTENER_TRANSPORTES_POR_FECHA,
  modificarDetalleTransporte,
} from "../../../services/transporteServicios";
import {
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Image,
  Placeholder,
} from "react-bootstrap";
import styled from "styled-components";
import CardTitle from "../../organisms/CardTitle";
import ServiciosBadges from "../../organisms/ServiciosBadges";
import { useLeerSectoresOrigen } from "../../../services/paisesServicios";
import { Typeahead } from "react-bootstrap-typeahead";
import { Option } from "react-bootstrap-typeahead/types/types";
import TimePicker from "react-time-picker";
import SuccessButton from "../../organisms/SuccessButton";
import SaveIcon from "@mui/icons-material/Save";
import { Controller, useForm } from "react-hook-form";
import ServerErrorModal from "../../organisms/ServerErrorModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { buildValueChange } from "../../../utils/ValueChange";
import { useLeerFoto } from "../../../services/mascotasServicios";

interface Model {
  sector: { id: string; nombre: string }[];
  hora: string;
  direccion: string;
  observaciones?: string;
}

function EdicionDetalleModal({
  idHoja,
  detalle,
  onCloseClick,
}: {
  idHoja: string;
  detalle: DetalleTransporteDeFecha;
  onCloseClick: () => void;
}) {
  const queryClient = useQueryClient();

  const {
    data: fotoMascota,
    isLoading: fotoMascotaIsLoading,
    isSuccess: fotoMascotaIsSuccess,
  } = useLeerFoto(detalle.mascota.id);

  const {
    isSuccess: sectoresIsSuccess,
    data: sectoresData,
    error: sectoresError,
  } = useLeerSectoresOrigen();

  const edicion = useMutation(modificarDetalleTransporte, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_OBTENER_TRANSPORTES_POR_FECHA],
      });
      onCloseClick();
    },
  });

  const defaultValues: Model = {
    sector: [{ id: detalle.sector.id, nombre: detalle.sector.nombre }],
    hora: detalle.hora,
    direccion: detalle.direccion,
    observaciones: detalle.observaciones,
  };

  const {
    handleSubmit,
    control,
    formState: { isDirty, dirtyFields },
  } = useForm<Model>({
    defaultValues,
    mode: "all",
  });

  const sectores: Option[] = useMemo(() => {
    if (sectoresIsSuccess && sectoresData) {
      return sectoresData.map((s) => ({ id: s.id, nombre: s.nombre }));
    }
    return [];
  }, [sectoresIsSuccess, sectoresData]);

  const onSubmit = (model: Model) => {
    if (!isDirty) return;

    const modificaciones: ModificacionesDetalleTransporte = {
      idHoja,
      id: detalle.id,
      tipo: detalle.tipo,
      direccion: dirtyFields.direccion
        ? buildValueChange(detalle.direccion, model.direccion)
        : undefined,
      hora: dirtyFields.hora
        ? buildValueChange(detalle.hora, model.hora)
        : undefined,
      sector: dirtyFields.sector
        ? buildValueChange(detalle.sector.id, model.sector[0].id)
        : undefined,
      observaciones: dirtyFields.observaciones
        ? buildValueChange(detalle.observaciones, model.observaciones)
        : undefined,
    };

    edicion.mutate(modificaciones);
  };

  const error = sectoresError;

  if (error) {
    return <ServerErrorModal />;
  }

  return (
    <Modal show={true} centered>
      <Modal.Body>
        <StyledCard>
          <StyledCardHeader className="mt-3">
            <CardTitle>{"Detalles de transporte"}</CardTitle>
          </StyledCardHeader>
          <StyledCardBody>
            <section>
              {fotoMascota && fotoMascotaIsSuccess && (
                <div className="d-flex mb-3 justify-content-center align-items-center">
                  <StyledAvatar
                    src={fotoMascota}
                    className="border border-3 border-primary"
                    roundedCircle
                    fluid
                  />
                </div>
              )}
              {fotoMascotaIsLoading && (
                <div className="d-flex mb-3 justify-content-center align-items-center">
                  <Placeholder
                    as={"div"}
                    style={{ width: 200, height: 200, borderRadius: "50%" }}
                    bg="light"
                  />
                </div>
              )}
              <div className="mb-3">
                <b>{"Mascota: "}</b>
                {detalle.mascota.nombre}
              </div>
              <div className="mb-3">
                <b>{"Propetario: "}</b>
                {detalle.propietario}
              </div>
              <div className="mb-3">
                <b>{"Raza: "}</b>
                {detalle.mascota.raza}
              </div>
              <div className="mb-3">
                <b>{"Edad: "}</b>
                {detalle.mascota.edad}
              </div>
              <div className="mb-3">
                <b>{"Celular: "}</b>
                {detalle.celular}
              </div>
              <div className="mb-3">
                <b>{"Servicios: "}</b>
                <ServiciosBadges servicios={detalle.servicios} />
              </div>
              <div className="mb-3">
                <b>{"¿Transportada?: "}</b>
                {detalle.transportado ? "Si" : "No"}
              </div>
            </section>

            <hr />
            <section className="mt-3">
              <Form id="detalle-form" onSubmit={handleSubmit(onSubmit)}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <b>{"Dirección: "}</b>
                  </Form.Label>
                  <Controller
                    control={control}
                    name="direccion"
                    rules={{
                      required: true,
                      minLength: 1,
                    }}
                    render={({
                      field: { onChange, onBlur, value, ref },
                      fieldState: { invalid },
                    }) => (
                      <Form.Control
                        ref={ref}
                        type="text"
                        value={value}
                        onChange={onChange}
                        isInvalid={invalid}
                        onBlur={onBlur}
                        required
                      />
                    )}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <b>{"Sector: "}</b>
                  </Form.Label>
                  <Controller
                    control={control}
                    name="sector"
                    rules={{
                      required: true,
                      minLength: 1,
                    }}
                    render={({
                      field: { onChange, ref },
                      fieldState: { invalid },
                    }) => (
                      <Typeahead
                        defaultSelected={defaultValues.sector}
                        ref={ref}
                        clearButton
                        id="input-sector"
                        options={sectores}
                        labelKey="nombre"
                        onChange={onChange}
                        isInvalid={invalid}
                      />
                    )}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <b>{"Hora: "}</b>
                  </Form.Label>
                  <Controller
                    control={control}
                    name="hora"
                    rules={{
                      required: true,
                      minLength: 1,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <TimePicker
                        value={value}
                        className="form-control"
                        onChange={onChange}
                      />
                    )}
                  />
                </Form.Group>
              </Form>
              <Form.Group className="mb-3">
                <Form.Label>
                  <b>{"Observaciones: "}</b>
                </Form.Label>
                <Controller
                  control={control}
                  name="observaciones"
                  rules={{
                    required: false,
                  }}
                  render={({
                    field: { onChange, onBlur, value, ref },
                    fieldState: { invalid },
                  }) => (
                    <Form.Control
                      ref={ref}
                      as="textarea"
                      rows={3}
                      value={value}
                      onChange={onChange}
                      isInvalid={invalid}
                      onBlur={onBlur}
                    />
                  )}
                />
              </Form.Group>
            </section>
          </StyledCardBody>
        </StyledCard>
      </Modal.Body>
      <Modal.Footer>
        <SuccessButton
          variant="success"
          type="submit"
          form="detalle-form"
          disabled={!isDirty}
        >
          {false && <Spinner animation="border" variant="light" size="sm" />}
          {true && (
            <SaveIcon
              sx={{ fontSize: 20, paddingBottom: "4px", paddingRight: "2px" }}
            />
          )}
          Guardar
        </SuccessButton>
        <Button variant="danger" onClick={onCloseClick}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const StyledCard = styled(Card)`
  background-color: #fff;
  transition: all 0.5s ease-in-out;
  position: relative;
  border: 0px solid transparent;
  border-radius: 0.25rem;
  box-shadow: 0px 0px 13px 0px rgb(82 63 105 / 5%);
`;

const StyledCardBody = styled(Card.Body)`
  padding: 1.25rem;
`;

const StyledCardHeader = styled(Card.Header)`
  border-color: #eaeaea;
  position: relative;
  background-color: inherit;
  display: flex;
  justify-content: start;
  align-items: center;
  border: 0px;
`;

const StyledAvatar = styled(Image)`
  object-fit: cover;
  width: 200px;
  height: 200px;
`;

export default EdicionDetalleModal;
